/**
 * Output Schemas Panel Component
 * List and manage centralized JSON output schema configurations
 * Includes AI-assisted schema generation from example JSON
 */

import React, { useState, useMemo } from 'react';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Loading, SchemaFormFields } from '@/components/common';
import type { SchemaFormData } from '@/components/common';
import {
  Plus,
  AlertCircle,
  Trash2,
  Edit2,
  Search,
  FileJson,
  Globe,
  User,
  Copy,
  Check,
  ToggleLeft,
  ToggleRight,
} from 'lucide-react';
import {
  useOutputSchemas,
  useCreateOutputSchema,
  useUpdateOutputSchema,
  useDeleteOutputSchema,
  useToggleOutputSchema,
} from '../hooks/useOutputSchemas';
import { useAgentsListQuery } from '@/lib/queries/useAgentsQuery';
import { useI18n } from '@/i18n';
import type {
  OutputSchema,
  OutputSchemaCreate,
  OutputSchemaUpdate,
  OutputSchemaScope,
} from '@/types/outputSchema';

// ============================================================================
// Schema Card Component
// ============================================================================

interface OutputSchemaCardProps {
  schema: OutputSchema;
  onEdit: (schema: OutputSchema) => void;
  onDelete: (key: string) => void;
  onToggle: (key: string, enabled: boolean) => void;
  isEditing?: boolean;
}

const OutputSchemaCard: React.FC<OutputSchemaCardProps> = ({
  schema,
  onEdit,
  onDelete,
  onToggle,
  isEditing,
}) => {
  const { messages } = useI18n();
  const t = messages.systemSettings?.outputSchemas;
  const [copied, setCopied] = useState(false);

  const handleCopyKey = () => {
    navigator.clipboard.writeText(schema.key);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Card className={`p-4 transition-colors ${isEditing ? 'ring-2 ring-blue-500 bg-blue-50/30' : ''}`}>
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-3 flex-1">
          <div className="p-2 bg-gray-100 rounded-lg flex items-center justify-center w-10 h-10">
            <FileJson className="w-5 h-5 text-gray-600" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="font-medium text-gray-900">{schema.name}</h3>
              {schema.name_zh && (
                <span className="text-sm text-gray-500">({schema.name_zh})</span>
              )}
              <span
                className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                  schema.scope === 'global'
                    ? 'bg-blue-100 text-blue-800'
                    : 'bg-purple-100 text-purple-800'
                }`}
              >
                {schema.scope === 'global' ? (
                  <span className="flex items-center gap-1">
                    <Globe className="w-3 h-3" />
                    Global
                  </span>
                ) : (
                  <span className="flex items-center gap-1">
                    <User className="w-3 h-3" />
                    {schema.agent_type}
                  </span>
                )}
              </span>
              <span
                className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                  schema.enabled
                    ? 'bg-green-100 text-green-800'
                    : 'bg-gray-100 text-gray-600'
                }`}
              >
                {schema.enabled ? 'Enabled' : 'Disabled'}
              </span>
            </div>
            <div className="flex items-center gap-2 mt-1">
              <code className="text-sm text-gray-500 bg-gray-50 px-2 py-0.5 rounded font-mono">
                {schema.key}
              </code>
              <button
                onClick={handleCopyKey}
                className="text-gray-400 hover:text-gray-600 transition-colors"
                title="Copy key"
              >
                {copied ? (
                  <Check className="w-3.5 h-3.5 text-green-500" />
                ) : (
                  <Copy className="w-3.5 h-3.5" />
                )}
              </button>
            </div>
            {schema.description && (
              <p className="text-sm text-gray-600 mt-2">{schema.description}</p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 ml-4">
          {isEditing ? (
            <span className="text-xs text-blue-600 font-medium px-2">{t?.editing || 'Editing...'}</span>
          ) : (
            <>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onToggle(schema.key, !schema.enabled)}
                title={schema.enabled ? 'Disable' : 'Enable'}
              >
                {schema.enabled ? (
                  <ToggleRight className="w-4 h-4 text-green-600" />
                ) : (
                  <ToggleLeft className="w-4 h-4 text-gray-400" />
                )}
              </Button>
              <Button variant="ghost" size="sm" onClick={() => onEdit(schema)}>
                <Edit2 className="w-4 h-4" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  const confirmMsg = (t?.confirmDelete || 'Delete output schema "{name}"?').replace('{name}', schema.key);
                  if (confirm(confirmMsg)) {
                    onDelete(schema.key);
                  }
                }}
              >
                <Trash2 className="w-4 h-4 text-red-500" />
              </Button>
            </>
          )}
        </div>
      </div>
    </Card>
  );
};

// ============================================================================
// Schema Form Component (using shared SchemaFormFields)
// ============================================================================

interface OutputSchemaFormProps {
  schema?: OutputSchema;
  onSave: (data: OutputSchemaCreate | OutputSchemaUpdate, isNew: boolean) => void;
  onCancel: () => void;
  isLoading?: boolean;
}

const OutputSchemaForm: React.FC<OutputSchemaFormProps> = ({
  schema,
  onSave,
  onCancel,
  isLoading,
}) => {
  const { messages } = useI18n();
  const t = messages.systemSettings?.outputSchemas;
  const isNew = !schema;
  const { data: agents } = useAgentsListQuery(true);

  // Form state for shared fields
  const [formData, setFormData] = useState<SchemaFormData>({
    key: schema?.key || '',
    name: schema?.name || '',
    description: schema?.description || null,
    json_schema: schema?.json_schema || {
      "$schema": "http://json-schema.org/draft-07/schema#",
      "type": "object",
      "properties": {},
      "required": []
    },
    example_output: schema?.example_output || null,
    format_instruction: schema?.format_instruction || '请严格按以下 JSON 格式输出：\n```json\n{schema}\n```',
  });

  // Additional fields specific to OutputSchema
  const [nameZh, setNameZh] = useState(schema?.name_zh || '');
  const [descriptionZh, setDescriptionZh] = useState(schema?.description_zh || '');
  const [scope, setScope] = useState<OutputSchemaScope>(schema?.scope || 'global');
  const [agentType, setAgentType] = useState(schema?.agent_type || '');
  const [enabled, setEnabled] = useState(schema?.enabled ?? true);
  const [displayOrder, setDisplayOrder] = useState(schema?.display_order?.toString() || '0');

  // Validation state
  const [jsonError, setJsonError] = useState<string | null>(null);
  const [exampleError, setExampleError] = useState<string | null>(null);

  const handleFormDataChange = (updates: Partial<SchemaFormData>) => {
    setFormData(prev => ({ ...prev, ...updates }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (jsonError || exampleError) {
      return;
    }

    const isKeyValid = /^[a-z][a-z0-9_]*$/.test(formData.key);
    if (!isKeyValid && isNew) {
      return;
    }

    const data: OutputSchemaCreate = {
      key: isNew ? formData.key : schema!.key,
      name: formData.name,
      name_zh: nameZh || null,
      description: formData.description || null,
      description_zh: descriptionZh || null,
      json_schema: formData.json_schema,
      example_output: formData.example_output || null,
      format_instruction: formData.format_instruction,
      scope,
      agent_type: scope === 'agent' ? agentType : null,
      enabled,
      display_order: parseInt(displayOrder, 10) || 0,
    };

    onSave(data, isNew);
  };

  const isKeyValid = /^[a-z][a-z0-9_]*$/.test(formData.key);

  return (
    <Card className="p-6">
      <h3 className="text-lg font-medium mb-4">
        {isNew ? (t?.addNew || 'Add Output Schema') : (t?.editTitle || 'Edit Output Schema')}
      </h3>
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Shared Schema Fields (AI Panel, Key, Name, Description, JSON Schema, Example, Format Instruction) */}
        <SchemaFormFields
          data={formData}
          onChange={handleFormDataChange}
          isKeyEditable={isNew}
          jsonSchemaError={jsonError}
          exampleError={exampleError}
          onJsonSchemaErrorChange={setJsonError}
          onExampleErrorChange={setExampleError}
        />

        {/* Additional Fields: Chinese Names */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t?.nameChinese || 'Name (Chinese)'}
            </label>
            <Input
              value={nameZh}
              onChange={(e) => setNameZh(e.target.value)}
              placeholder="e.g., 幸运符文案"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t?.descriptionZh || 'Description (Chinese)'}
            </label>
            <Input
              value={descriptionZh}
              onChange={(e) => setDescriptionZh(e.target.value)}
              placeholder="描述"
            />
          </div>
        </div>

        {/* Scope and Agent Type */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t?.scope || 'Scope'}
            </label>
            <select
              value={scope}
              onChange={(e) => setScope(e.target.value as OutputSchemaScope)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="global">{t?.scopeGlobalOption || 'Global (All Agents)'}</option>
              <option value="agent">{t?.scopeAgentOption || 'Agent-Specific'}</option>
            </select>
          </div>
          {scope === 'agent' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Agent *
              </label>
              <select
                value={agentType}
                onChange={(e) => setAgentType(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                required={scope === 'agent'}
              >
                <option value="">{t?.selectAgent || 'Select Agent...'}</option>
                {agents?.map((agent) => (
                  <option key={agent.agent_type} value={agent.agent_type}>
                    {agent.name} ({agent.agent_type})
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>

        {/* Display Order */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t?.displayOrder || 'Display Order'}
            </label>
            <Input
              type="number"
              value={displayOrder}
              onChange={(e) => setDisplayOrder(e.target.value)}
              placeholder="0"
            />
          </div>
          <div className="flex items-end pb-2">
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="enabled"
                checked={enabled}
                onChange={(e) => setEnabled(e.target.checked)}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <label htmlFor="enabled" className="text-sm font-medium text-gray-700">
                {t?.enabled || 'Enabled'}
              </label>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-2 pt-4 border-t">
          <Button type="button" variant="secondary" onClick={onCancel}>
            {t?.cancel || 'Cancel'}
          </Button>
          <Button type="submit" disabled={isLoading || !!jsonError || !!exampleError || (!isKeyValid && isNew)}>
            {isLoading ? (t?.saving || 'Saving...') : (t?.save || 'Save')}
          </Button>
        </div>
      </form>
    </Card>
  );
};

// ============================================================================
// Main Panel Component
// ============================================================================

export const OutputSchemasPanel: React.FC = () => {
  const { messages } = useI18n();
  const t = messages.systemSettings.outputSchemas;

  const [editingSchema, setEditingSchema] = useState<OutputSchema | null>(null);
  const [isAdding, setIsAdding] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [scopeFilter, setScopeFilter] = useState<'all' | 'global' | 'agent'>('all');
  const [enabledFilter, setEnabledFilter] = useState<'all' | 'enabled' | 'disabled'>('all');

  const { data, isLoading, error } = useOutputSchemas();
  const createSchema = useCreateOutputSchema();
  const updateSchema = useUpdateOutputSchema();
  const deleteSchema = useDeleteOutputSchema();
  const toggleSchema = useToggleOutputSchema();

  // Filter schemas
  const filteredSchemas = useMemo(() => {
    if (!data?.schemas) return [];

    return data.schemas.filter((schema) => {
      // Search filter
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const matchesSearch =
          schema.key.toLowerCase().includes(query) ||
          schema.name.toLowerCase().includes(query) ||
          schema.name_zh?.toLowerCase().includes(query) ||
          schema.description?.toLowerCase().includes(query);
        if (!matchesSearch) return false;
      }

      // Scope filter
      if (scopeFilter !== 'all' && schema.scope !== scopeFilter) return false;

      // Enabled filter
      if (enabledFilter === 'enabled' && !schema.enabled) return false;
      if (enabledFilter === 'disabled' && schema.enabled) return false;

      return true;
    });
  }, [data?.schemas, searchQuery, scopeFilter, enabledFilter]);

  const handleSave = async (
    formData: OutputSchemaCreate | OutputSchemaUpdate,
    isNew: boolean
  ) => {
    try {
      if (isNew) {
        await createSchema.mutateAsync(formData as OutputSchemaCreate);
      } else {
        await updateSchema.mutateAsync({
          key: (formData as OutputSchemaCreate).key,
          data: formData as OutputSchemaUpdate,
        });
      }
      setEditingSchema(null);
      setIsAdding(false);
    } catch (error) {
      alert(`${t?.saveFailed || 'Failed to save'}: ${error}`);
    }
  };

  const handleDelete = async (key: string) => {
    try {
      await deleteSchema.mutateAsync(key);
    } catch (error) {
      alert(`${t?.deleteFailed || 'Failed to delete'}: ${error}`);
    }
  };

  const handleToggle = async (key: string, enabled: boolean) => {
    try {
      await toggleSchema.mutateAsync({ key, enabled });
    } catch (error) {
      alert(`${t?.toggleFailed || 'Failed to toggle'}: ${error}`);
    }
  };

  if (isLoading) {
    return <Loading message={t?.loading || 'Loading output schemas...'} />;
  }

  if (error) {
    return (
      <div className="flex items-center gap-2 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
        <AlertCircle className="w-5 h-5" />
        <span>
          {t?.loadFailed || 'Failed to load output schemas'}: {error.message}
        </span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">
            {t?.title || 'Output Schemas'}
          </h2>
          <p className="text-sm text-gray-600 mt-1">
            {t?.description ||
              'Manage centralized JSON output schema definitions for agent responses'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            onClick={() => {
              setEditingSchema(null);
              setIsAdding(true);
            }}
            disabled={isAdding}
          >
            <Plus className="w-4 h-4 mr-2" />
            {t?.addSchema || 'Add Schema'}
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={t?.searchPlaceholder || 'Search schemas...'}
            className="pl-10"
          />
        </div>
        <select
          value={scopeFilter}
          onChange={(e) => setScopeFilter(e.target.value as typeof scopeFilter)}
          className="px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 text-sm"
        >
          <option value="all">{t?.scopeAll || 'All Scopes'}</option>
          <option value="global">{t?.scopeGlobal || 'Global Only'}</option>
          <option value="agent">{t?.scopeAgent || 'Agent-Specific'}</option>
        </select>
        <select
          value={enabledFilter}
          onChange={(e) =>
            setEnabledFilter(e.target.value as typeof enabledFilter)
          }
          className="px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 text-sm"
        >
          <option value="all">{t?.statusAll || 'All Status'}</option>
          <option value="enabled">{t?.statusEnabled || 'Enabled'}</option>
          <option value="disabled">{t?.statusDisabled || 'Disabled'}</option>
        </select>
      </div>

      {/* Add new form - renders at the top */}
      {isAdding && (
        <OutputSchemaForm
          key="new"
          onSave={handleSave}
          onCancel={() => setIsAdding(false)}
          isLoading={createSchema.isPending}
        />
      )}

      {/* Schema List with inline edit form */}
      {filteredSchemas.length > 0 ? (
        <div className="space-y-3">
          {filteredSchemas.map((schema) => (
            <React.Fragment key={schema.key}>
              <OutputSchemaCard
                schema={schema}
                onEdit={(s) => {
                  setIsAdding(false);
                  setEditingSchema(s);
                }}
                onDelete={handleDelete}
                onToggle={handleToggle}
                isEditing={editingSchema?.key === schema.key}
              />
              {/* Inline edit form - renders below the card being edited */}
              {editingSchema?.key === schema.key && (
                <OutputSchemaForm
                  key={`edit-${schema.key}`}
                  schema={editingSchema}
                  onSave={handleSave}
                  onCancel={() => setEditingSchema(null)}
                  isLoading={updateSchema.isPending}
                />
              )}
            </React.Fragment>
          ))}
        </div>
      ) : !isAdding ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg border border-gray-200">
          <FileJson className="w-12 h-12 text-gray-400 mx-auto mb-3" />
          <p className="text-gray-600">
            {searchQuery
              ? t?.noSchemasMatch || 'No schemas match your search'
              : t?.noSchemas || 'No output schemas configured'}
          </p>
          {!searchQuery && (
            <button
              onClick={() => setIsAdding(true)}
              className="mt-2 text-blue-600 hover:text-blue-700 font-medium"
            >
              {t?.addFirstSchema || 'Add your first schema'}
            </button>
          )}
        </div>
      ) : null}

      {/* Info Section */}
      <Card className="p-4 bg-blue-50 border-blue-200">
        <h4 className="font-medium text-blue-900 mb-2">
          {t?.aboutTitle || 'About Output Schemas'}
        </h4>
        <ul className="space-y-1 text-sm text-blue-800 list-disc list-inside">
          <li>{t?.aboutItem1 || 'Global schemas are available to all agents'}</li>
          <li>
            {t?.aboutItem2 ||
              'Agent-specific schemas are only available to the specified agent type'}
          </li>
          <li>
            {t?.aboutItem3 ||
              'Use {schema} placeholder in format_instruction to inject the JSON schema'}
          </li>
          <li>
            {t?.aboutItem4 ||
              'Disabled schemas are hidden from agent selection dropdowns'}
          </li>
        </ul>
      </Card>

      {/* Metadata */}
      {data && (
        <div className="text-sm text-gray-500">
          <p>
            {t?.totalSchemas || 'Total schemas'}: <span>{data.total}</span>
            {filteredSchemas.length !== data.total && (
              <span className="ml-2">
                ({t?.showing || 'Showing'}: {filteredSchemas.length})
              </span>
            )}
          </p>
        </div>
      )}
    </div>
  );
};
